/** @jsxRuntime classic */
/** @jsx jsx */
import React, { useState, useEffect, useMemo } from 'react';
import { css, jsx } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useInterval } from 'react-use';
import Chip from '@mui/material/Chip';
import Text from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import HoverPopover from 'material-ui-popup-state/HoverPopover';
import PopupState, { bindPopover, bindHover } from 'material-ui-popup-state';
import { UpgradeButton } from '../common/Buttons';

import RocketLaunchIcon from '@mui/icons-material/RocketLaunchOutlined';
import { useUserQuery } from '../../actions/UserActions';
import { useCreditsQuery } from '../../actions/CreditsActions';
import { track } from '../../utils/track';

function getTimeLeft() {
  const now = new Date();

  const currentYear = now.getUTCFullYear();
  const currentMonth = now.getUTCMonth();

  const nextMonth = new Date(
    Date.UTC(currentYear, currentMonth + 1, 0, 23, 59, 59, 999)
  );

  const diff = nextMonth - now;

  return diff;
}

export default function AccountLimits({ minimized }) {
  const { t } = useTranslation();
  const { user } = useUserQuery();
  const isGuest = !user || !user.role || user.role === 'guest';

  const { credits } = useCreditsQuery({ enabled: !isGuest });

  const isTrial = user?.workspace?.type === 'trial';
  const isStripe = user?.workspace?.type === 'stripe';
  const isEnterprise = user?.workspace?.type === 'enterprise';
  const isV2 = user?.workspace?.account_type === 'v2';
  const isAdmin = user?.workspace_role === 'admin';

  const [timeLeft, setTimeLeft] = useState(0);

  useEffect(() => {
    setTimeLeft(getTimeLeft());
  }, []);

  useInterval(() => {
    setTimeLeft(getTimeLeft());
  }, 10000);

  const timeLeftString = useMemo(() => {
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);

    if (days === 1) {
      return t('1 dag');
    }

    if (days > 1) {
      return t('{{days}} dagar', { days });
    }

    if (hours === 1) {
      return t('1 timme');
    }

    if (hours > 1) {
      return t('{{hours}} timmar', { hours });
    }

    if (minutes === 1) {
      return t('1 minut');
    }

    if (minutes > 1) {
      return t('{{minutes}} minuter', { minutes });
    }

    return t('{{seconds}} sekunder', { seconds });
  }, [timeLeft, t]);

  const exports = credits?.export
    ? {
        available: credits.export.credits,
        total: credits.export.credits_monthly_refill,
      }
    : null;

  const generation = credits?.generation
    ? {
        available: credits.generation.credits,
        total: credits.generation.credits_monthly_refill,
      }
    : null;

  if (isGuest || !isAdmin) {
    return null;
  }

  if (!isV2) {
    return null;
  }

  if (!isStripe && !isTrial && false) {
    return null;
  }

  function renderAccountLimit(name, available, limit) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 8px;
          padding: 0 8px;
        `}
      >
        <div
          css={css`
            display: flex;
            gap: 4px;
            justify-content: space-between;
            align-items: baseline;
          `}
        >
          <Text
            variant="body1"
            css={css`
              color: rgba(0, 0, 0, 0.6);
              font-family: Lato;
              font-size: 14px;
              font-weight: 700;
              line-height: 150%; /* 21px */
              letter-spacing: 0.15px;
            `}
          >
            {name}
          </Text>
          {available === 0 && limit > 0 && (
            <Chip
              label={t('Fylls på om {{timeLeft}}', {
                limit,
                timeLeft: timeLeftString,
              })}
              size="small"
              css={css`
                justify-self: flex-end;
                margin-right: auto;
                font-size: 13px;
                font-weight: 400;
                letter-spacing: 0.16px;
              `}
            />
          )}
          {available !== -1 && limit !== -1 ? (
            <Text
              variant="body2"
              css={css`
                color: rgba(0, 0, 0, 0.6);
                font-family: Lato;
                font-size: 14px;
                font-weight: 700;
                line-height: 150%; /* 21px */
                letter-spacing: 0.15px;
              `}
            >
              {available} / {limit}
            </Text>
          ) : (
            <Text
              variant="body2"
              css={css`
                color: rgba(0, 0, 0, 0.6);
                font-family: Lato;
                font-size: 14px;
                font-weight: 700;
                line-height: 150%; /* 21px */
                letter-spacing: 0.15px;
              `}
            >
              {t('Obegränsat')}
            </Text>
          )}
        </div>
        <LinearProgress
          variant="determinate"
          value={(Math.min(available, limit) / limit) * 100}
          css={css`
            height: 8px;
            border-radius: 8px;
            background-color: rgba(0, 0, 0, 0.08);

            .MuiLinearProgress-bar {
              background-color: rgba(0, 0, 0, 0.54);
            }

            .MuiLinearProgress-root {
              background-color: rgba(0, 0, 0, 0.08);
            }
          `}
        />
      </div>
    );
  }

  return (
    <PopupState variant="popover" popupId="account-limits-popover">
      {(popupState) => (
        <>
          <div
            {...bindHover(popupState)}
            css={css`
              display: flex;
              flex-direction: column;
              gap: 4px;
            `}
          >
            <IconButton
              component={Link}
              to={isEnterprise ? '#' : '/profile/billing'}
              color="text"
              variant="outlined"
              css={css`
                width: 65px;
                height: 65px;
                border-radius: 8px;
                background: linear-gradient(
                  269deg,
                  rgba(251, 188, 4, 0.29) 0%,
                  rgba(255, 255, 255, 0.54) 58.1%
                );
                box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.12),
                  0px 1px 1px 0px rgba(0, 0, 0, 0.14),
                  0px 2px 1px -1px rgba(0, 0, 0, 0.2);
                overflow: hidden;

                ::before {
                  content: '';
                  position: absolute;
                  top: 0;
                  left: -100%;
                  width: 100%;
                  height: 100%;
                  background: linear-gradient(
                    to right,
                    rgba(255, 255, 255, 0) 0%,
                    rgba(255, 255, 255, 0.8) 50%,
                    rgba(255, 255, 255, 0) 100%
                  );
                  transition: all 0.5s;
                  pointer-events: none;
                  animation: shine 10s infinite;
                }

                @keyframes shine {
                  0% {
                    left: -100%;
                  }
                  90% {
                    left: -100%;
                  }
                  100% {
                    left: 100%;
                  }
                }
              `}
            >
              <RocketLaunchIcon />
            </IconButton>
            {user?.workspace?.type !== 'enterprise' && (
              <Text
                css={css`
                  color: rgba(0, 0, 0, 0.6);
                  font-size: 10px;
                  text-align: center;
                  font-weight: 700;
                  line-height: 12px;
                  letter-spacing: 0.15px;
                `}
              >
                {t('Uppgradera')}
              </Text>
            )}
          </div>
          <HoverPopover
            {...bindPopover(popupState)}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
          >
            <div
              css={css`
                box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.12),
                  0px 1px 1px 0px rgba(0, 0, 0, 0.14),
                  0px 2px 1px -1px rgba(0, 0, 0, 0.2);
                border-radius: 8px;
                background: #fff9eb;
                padding: 8px;
                display: flex;
                flex-direction: column;
                gap: 8px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: baseline;
                `}
              >
                <Text
                  variant="h6"
                  css={css`
                    font-feature-settings: 'clig' off, 'liga' off;
                    font-family: Lato;
                    font-size: 15px;
                    font-weight: 700;
                    line-height: 150%; /* 22.5px */
                    letter-spacing: 0.15px;
                    padding: 4px 8px;
                  `}
                >
                  {isTrial ? t('Gratiskonto') : t('Abonnemang')}
                </Text>
                <Text
                  variant="body2"
                  color="textSecondary"
                  css={css`
                    font-family: Lato;
                    font-size: 12px;
                    font-weight: 500;
                    line-height: 150%; /* 21px */
                    letter-spacing: 0.15px;
                    padding: 4px 8px;
                  `}
                >
                  {t('Antal kvar')}
                </Text>
              </div>
              {exports &&
                renderAccountLimit(
                  t('Exporter'),
                  exports.available,
                  exports.total
                )}
              {generation &&
                renderAccountLimit(
                  t('AI-svar'),
                  generation.available,
                  generation.total
                )}
              {user?.workspace?.type !== 'enterprise' && isAdmin && (
                <UpgradeButton
                  event="account_limits_upgrade_button"
                  text={t('Uppgradera för mer')}
                />
              )}
            </div>
          </HoverPopover>
        </>
      )}
    </PopupState>
  );
}
